'use client'

import React from 'react'
import type { HtmlBlock as BlockProps } from '@/payload-types'
import { cn } from '@/utilities/cn'

// Dynamically import DOMPurify only when rendering in the browser
const getDOMPurify = async () => {
  if (typeof window === 'undefined') return null
  const { default: DOMPurify } = await import('isomorphic-dompurify')
  return DOMPurify
}

export const HtmlBlock: React.FC<BlockProps> = ({ html, className, trusted }) => {
  const [processedHtml, setProcessedHtml] = React.useState<string>(html || '')

  React.useEffect(() => {
    // When trusted is true, use HTML directly without sanitization
    // Only use this for content from trusted sources to avoid XSS attacks
    if (trusted) {
      setProcessedHtml(html || '')
      return
    }

    // Sanitize HTML in the browser to prevent XSS attacks
    getDOMPurify().then(DOMPurify => {
      if (!DOMPurify) {
        // During build, skip sanitization
        setProcessedHtml(html || '')
        return
      }

      const sanitized = DOMPurify.sanitize(html || '', {
        // Allow common safe HTML elements and attributes
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'blockquote', 'code', 'pre',
          'img', 'figure', 'figcaption',
          'table', 'thead', 'tbody', 'tr', 'th', 'td',
          'div', 'span', 'section', 'article',
          'b', 'i', 'strike', 'del', 'ins',
        ],
        ALLOWED_ATTR: [
          'href', 'title', 'target', 'rel',
          'src', 'alt', 'width', 'height',
          'class', 'id',
          'colspan', 'rowspan',
        ],
        // Ensure links open securely
        ADD_ATTR: ['target'],
        ALLOW_DATA_ATTR: false,
      })
      setProcessedHtml(sanitized)
    })
  }, [html, trusted])

  return (
    <div
      className={cn(className)}
      dangerouslySetInnerHTML={{ __html: processedHtml as TrustedHTML }}
    />
  )
}
